import { RefObject, useEffect, useRef, useState } from 'react';
import { monitoringConfig } from '../config/monitoring';
import { createBrowserPresenceDetector } from '../services/presenceDetector';
import { ObservationCondition } from '../utils/observations';
import { CandidatePresenceMonitor, PresenceCapability, PresenceDetectorFactory, PresenceDiagnostics, PresenceState } from '../utils/candidatePresence';

interface CandidatePresenceOptions {
  enabled: boolean;
  videoRef: RefObject<HTMLVideoElement>;
  stream?: MediaStream;
  record: (condition: ObservationCondition) => void;
  detectorFactory?: PresenceDetectorFactory;
}

const initialDiagnostics: PresenceDiagnostics = {
  detectorReady: false,
  state: 'warming-up',
  failureCount: 0
};

export function useCandidatePresence(options: CandidatePresenceOptions) {
  const { detectorFactory: configuredDetectorFactory, enabled, record, stream, videoRef } = options;
  const [capability, setCapability] = useState<PresenceCapability>('checking');
  const [presenceState, setPresenceState] = useState<PresenceState>('warming-up');
  const diagnostics = useRef<PresenceDiagnostics>(initialDiagnostics);

  useEffect(() => {
    if (!enabled) {
      setCapability('checking');
      setPresenceState('warming-up');
      diagnostics.current = initialDiagnostics;
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    const presence = monitoringConfig.candidatePresence;
    const detectorFactory = configuredDetectorFactory ?? ((preferFallback) => createBrowserPresenceDetector({
      preferFallback,
      analysisWidthPx: presence.analysisWidthPx,
      analysisHeightPx: presence.analysisHeightPx,
      detectionTimeoutMs: presence.detectionTimeoutMs
    }));
    const logger = import.meta.env.DEV
      ? (event: string, details: PresenceDiagnostics) => console.debug('[ExamSphere presence]', event, details)
      : undefined;
    const monitor = new CandidatePresenceMonitor(presence, detectorFactory, Date.now(), logger);

    const schedule = () => {
      if (!cancelled) timer = window.setTimeout(() => void run(), presence.sampleIntervalMs);
    };
    const run = async () => {
      const track = stream?.getVideoTracks()[0];
      const video = track?.readyState === 'live' && !track.muted ? videoRef.current : null;
      const update = await monitor.sample(video, Date.now());
      if (cancelled) return;
      diagnostics.current = update;
      setCapability((current) => current === update.capability ? current : update.capability);
      setPresenceState((current) => current === update.state ? current : update.state);
      if (update.absence !== undefined) {
        record({
          type: 'candidate-absent',
          source: 'laptop-camera',
          present: update.absence,
          confirmationMs: 0,
          recoveryMs: 0,
          importance: 'important',
          message: 'Your face is outside the laptop camera view. Return to the required exam position.',
          confidence: update.absence ? 80 : undefined,
          technicalDetails: { detector: update.detector ?? 'unavailable', presenceState: update.state },
          now: Date.now()
        });
      }
      schedule();
    };

    void run();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      void monitor.dispose();
    };
  }, [configuredDetectorFactory, enabled, record, stream, videoRef]);

  return {
    presenceCapability: capability,
    presenceState,
    presenceDiagnostics: import.meta.env.DEV ? diagnostics.current : undefined
  };
}
